import SEO from '../pages/SEO';
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Hand } from "lucide-react";
import HandWrist from '../images/Hand&Wrist.jpg'

export default function HandWristPage() {
  const benefits = [
    "Detailed clinical and functional assessment",
    "Advanced diagnostic imaging and nerve studies",
    "Microsurgical techniques for precision",
    "Early mobilization protocols",
    "Customized hand therapy programs",
    "Splinting and orthotic support",
  ];

  const conditions = [
    {
      title: "Common Conditions",
      items: [
        "Carpal tunnel syndrome",
        "Trigger finger",
        "De Quervain's tenosynovitis",
        "Wrist and hand fractures",
        "Ligament injuries (TFCC tears)",
        "Arthritis of hand and wrist",
      ],
    },
    {
      title: "Surgical Procedures",
      items: [
        "Carpal tunnel release",
        "Trigger finger release",
        "Wrist arthroscopy",
        "Fracture fixation (K-wires, plates)",
        "Tendon repair and reconstruction",
        "Joint replacement and fusion",
      ],
    },
  ];

  const faqs = [
    {
      question: "What are the symptoms of carpal tunnel syndrome?",
      answer:
        "Common symptoms include numbness, tingling, and pain in the thumb, index, and middle fingers, especially at night. You may also experience weakness in grip strength and difficulty with fine motor tasks.",
    },
    {
      question: "How long is recovery after carpal tunnel surgery?",
      answer:
        "Most patients can perform light activities within 1–2 weeks. Full recovery typically takes 6–12 weeks depending on the procedure.",
    },
    {
      question: "Can trigger finger be treated without surgery?",
      answer:
        "Yes, many early cases respond to rest, splinting, medications, and steroid injections. Surgery is needed only if conservative treatment fails.",
    },
    {
      question: "What is the recovery time for wrist fractures?",
      answer:
        "Casting usually heals fractures in 6–8 weeks. Surgical cases may take 3–6 months, including physiotherapy for full recovery.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Hand & Wrist Surgeon in Hyderabad | Dr. Sujit Kumar"
        description="Dr. Sujit Kumar offers hand and wrist orthopedic care in Hyderabad for injuries, joint conditions, fractures and complex hand problems."
        keywords="hand surgeon Hyderabad, wrist surgeon Hyderabad, hand specialist Hyderabad, hand injury doctor, microsurgery Hyderabad"
        canonical="https://www.drsujitkumar.com/treatments/hand-wrist"
        robots="index, follow"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Hand & Wrist Care
            </h1>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Specialized treatment for hand and wrist injuries and conditions
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6">Comprehensive Hand & Wrist Treatment</h2>

              <div className="prose prose-lg max-w-none space-y-4">
                <p>
                  The hand and wrist are intricate structures essential for daily activities, work, and sports.
                  Conditions here can significantly reduce mobility and quality of life.
                </p>
                <p>
                  We treat everything from fractures and ligament injuries to carpal tunnel syndrome, tendon disorders,
                  and arthritis—using evidence-based treatment and advanced techniques.
                </p>
                <p>
                  With microsurgical precision and minimally invasive approaches, our goal is to restore full function
                  quickly and safely.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Our Expertise</h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Image */}
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src={HandWrist}
                alt="Hand and Wrist Examination"
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Conditions We Treat</h2>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {conditions.map((condition) => (
              <Card key={condition.title}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">{condition.title}</h3>

                  <ul className="space-y-2">
                    {condition.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Approach */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Our Treatment Approach</h2>

          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Comprehensive Evaluation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Includes range of motion testing, strength assessment, sensory exam, imaging, and nerve studies.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Non-Surgical Treatment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Splinting, injections, therapy, medications, and activity modification—ideal for tendon issues, early
                  carpal tunnel, and inflammation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Surgical Excellence</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Minimally invasive and microsurgical procedures for tendon repairs, fracture fixation, nerve release,
                  and joint preservation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Hand Therapy & Rehabilitation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Customized programs for restoring strength, motion, dexterity, and function with safe progressive
                  mobilization protocols.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Expert Hand & Wrist Care</h2>
          <p className="text-lg mb-8 text-muted-foreground max-w-2xl mx-auto">
            Schedule a consultation to get specialized care for your hand or wrist condition.
          </p>

          <Button size="lg" asChild>
            <Link to="/book-appointment">
              Book Appointment <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
